<template>
  <section class="dte-card dte-detail-block">
    <div class="dte-card__body" style="padding: 0;">
      <table class="dte-table dte-table--detail">
        <thead>
          <tr v-if="compact">
            <th class="dte-table__right" style="width: 46px;">{{ itemNumberLabel }}</th>
            <th>{{ descriptionLabel }}</th>
            <th class="dte-table__right" style="width: 72px;">{{ quantityLabel }}</th>
            <th class="dte-table__right" style="width: 94px;">{{ amountLabel }}</th>
          </tr>
          <tr v-else>
            <th class="dte-table__right" style="width: 40px;">{{ itemNumberLabel }}</th>
            <th v-if="showItemTypeColumn" style="width: 68px;">{{ itemTypeLabel }}</th>
            <th class="dte-table__right" style="width: 54px;">{{ quantityLabel }}</th>
            <th v-if="showUnitColumn" style="width: 72px;">{{ unitLabel }}</th>
            <th v-if="showCodeColumn" style="width: 72px;">{{ codeLabel }}</th>
            <th v-if="showAlternateCodeColumn" style="width: 84px;">{{ alternateCodeLabel }}</th>
            <th>{{ descriptionLabel }}</th>
            <th class="dte-table__right" style="width: 84px;">{{ unitPriceLabel }}</th>
            <th v-if="showDiscountColumn" class="dte-table__right" style="width: 72px;">{{ discountLabel }}</th>
            <th v-if="showExemptColumn" class="dte-table__right" style="width: 72px;">{{ exemptLabel }}</th>
            <th v-if="showNonSubjectColumn" class="dte-table__right" style="width: 78px;">{{ nonSubjectLabel }}</th>
            <th v-if="showNonTaxedColumn" class="dte-table__right" style="width: 78px;">{{ nonTaxableLabel }}</th>
            <th class="dte-table__right" style="width: 80px;">{{ amountLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in page.items" :key="`item-${page.number}-${item.fragmentKey || item.index}`">
            <td class="dte-table__right">{{ item._showLeadingValues === false ? "" : item.index }}</td>
            <template v-if="compact">
              <td class="dte-table__description">{{ item.description }}</td>
              <td class="dte-table__right">{{ item._showLeadingValues === false ? "" : item.quantity }}</td>
              <td class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(item.total) }}</td>
            </template>
            <template v-else>
              <td v-if="showItemTypeColumn">{{ item._showLeadingValues === false ? "" : (item.itemType || "---") }}</td>
              <td class="dte-table__right">{{ item._showLeadingValues === false ? "" : item.quantity }}</td>
              <td v-if="showUnitColumn">{{ item._showLeadingValues === false ? "" : (item.unit || "---") }}</td>
              <td v-if="showCodeColumn">{{ item._showLeadingValues === false ? "" : (item.code || "---") }}</td>
              <td v-if="showAlternateCodeColumn">{{ item._showLeadingValues === false ? "" : (item.barcode || "---") }}</td>
              <td class="dte-table__description">{{ item.description }}</td>
              <td class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(item.unitPrice) }}</td>
              <td v-if="showDiscountColumn" class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(item.discount) }}</td>
              <td v-if="showExemptColumn" class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(item.exempt) }}</td>
              <td v-if="showNonSubjectColumn" class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(item.nonSubject) }}</td>
              <td v-if="showNonTaxedColumn" class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(item.nonTaxed) }}</td>
              <td class="dte-table__right">{{ item._showLeadingValues === false ? "" : formatMoney(mainAmount(item)) }}</td>
            </template>
          </tr>
          <tr v-if="!page.items.length && !page.showTail">
            <td :colspan="compact ? 4 : fullColspan" class="dte-table__center dte-muted">
              Documento sin lineas de detalle
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import { formatMoney } from "../utils/dtePrintFormatters";
import { getDteTemplateLabel, isDteTemplateFieldVisible } from "@/helpers/dteTemplateSettings";

export default {
  name: "DteDetailTableSection",
  props: {
    documentData: {
      type: Object,
      required: true,
    },
    page: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    templateSettings() {
      return this.documentData.templateSettings || {};
    },
    showItemTypeColumn() {
      return (
        !this.compact &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "itemType")
      );
    },
    showUnitColumn() {
      return (
        !this.compact &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "unit") &&
        this.documentData.items.some((item) => item.unit)
      );
    },
    showCodeColumn() {
      return (
        !this.compact &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "code") &&
        this.documentData.items.some((item) => item.code)
      );
    },
    showAlternateCodeColumn() {
      return (
        !this.compact &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "alternateCode") &&
        this.documentData.items.some((item) => item.barcode)
      );
    },
    showDiscountColumn() {
      return (
        !this.compact &&
        this.documentData.meta.invoiceType !== "CDD" &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "discount")
      );
    },
    showExemptColumn() {
      return (
        !this.compact &&
        !["FEX", "CDD"].includes(this.documentData.meta.invoiceType) &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "exemptSales")
      );
    },
    showNonSubjectColumn() {
      return (
        !this.compact &&
        !["FEX", "CDD"].includes(this.documentData.meta.invoiceType) &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "nonSubjectSales")
      );
    },
    showNonTaxedColumn() {
      return (
        !this.compact &&
        ["FCF", "CCF", "FEX"].includes(this.documentData.meta.invoiceType) &&
        isDteTemplateFieldVisible(this.templateSettings, "detail", "nonTaxableSales")
      );
    },
    codeLabel() {
      return getDteTemplateLabel(this.templateSettings, "code", "Codigo");
    },
    itemNumberLabel() {
      return getDteTemplateLabel(this.templateSettings, "itemNumber", "Item");
    },
    itemTypeLabel() {
      return getDteTemplateLabel(this.templateSettings, "itemType", "Tipo item");
    },
    quantityLabel() {
      return getDteTemplateLabel(this.templateSettings, "quantity", this.compact ? "Cantidad" : "Cant.");
    },
    descriptionLabel() {
      return getDteTemplateLabel(this.templateSettings, "description", "Descripcion");
    },
    alternateCodeLabel() {
      return getDteTemplateLabel(this.templateSettings, "alternateCode", "Codigo alterno");
    },
    unitLabel() {
      return getDteTemplateLabel(this.templateSettings, "unit", "Unidad");
    },
    unitPriceLabel() {
      return getDteTemplateLabel(this.templateSettings, "unitPrice", "P. Unitario");
    },
    discountLabel() {
      return getDteTemplateLabel(this.templateSettings, "discount", "Desc.");
    },
    exemptLabel() {
      return getDteTemplateLabel(this.templateSettings, "exemptSales", "Exenta");
    },
    nonSubjectLabel() {
      return getDteTemplateLabel(this.templateSettings, "nonSubjectSales", "No sujeta");
    },
    nonTaxableLabel() {
      return getDteTemplateLabel(this.templateSettings, "nonTaxableSales", "No afecta");
    },
    amountLabel() {
      if (this.documentData.meta.invoiceType === "FEX") {
        return getDteTemplateLabel(this.templateSettings, "mainAmount", "Afecta");
      }

      if (this.documentData.meta.invoiceType === "CDD") {
        return getDteTemplateLabel(this.templateSettings, "mainAmount", "Donado");
      }

      return getDteTemplateLabel(
        this.templateSettings,
        "mainAmount",
        this.compact ? "Total" : "Gravada"
      );
    },
    fullColspan() {
      return 5 +
        (this.showItemTypeColumn ? 1 : 0) +
        (this.showUnitColumn ? 1 : 0) +
        (this.showCodeColumn ? 1 : 0) +
        (this.showAlternateCodeColumn ? 1 : 0) +
        (this.showDiscountColumn ? 1 : 0) +
        (this.showExemptColumn ? 1 : 0) +
        (this.showNonSubjectColumn ? 1 : 0) +
        (this.showNonTaxedColumn ? 1 : 0);
    },
  },
  methods: {
    formatMoney(value) {
      return formatMoney(value, this.documentData.meta.currency);
    },
    mainAmount(item) {
      if (this.documentData.meta.invoiceType === "CDD") {
        return item.total;
      }

      if (this.documentData.meta.invoiceType === "FEX") {
        return item.taxed || item.total;
      }

      return item.taxed;
    },
  },
};
</script>
